import { DomainError } from "./errors";
import { taxTotalsOf, type TaxItemResult, type TaxResult } from "./tax";

// ===== Eventos da NF-e (T2.4, RF-VEN-03): cancelamento, carta de correção, inutilização e devolução =====

/** Prazo do cancelamento desde a autorização (Ajuste SINIEF 07/05; prazo extemporâneo fica de fora). */
export const NFE_CANCEL_WINDOW_HOURS = 24;
/** Prazos por UF diferentes do padrão (nenhum cadastrado: confirmar com o contador). */
const CANCEL_WINDOW_BY_UF: Readonly<Record<string, number>> = {};
/** Prazo da carta de correção desde a autorização (30 dias). */
export const NFE_CORRECTION_WINDOW_HOURS = 720;
/** Sequência da CC-e (nSeqEvento) de 1 a 20 por nota. */
export const NFE_MAX_CORRECTIONS = 20;
/** Maior faixa inutilizada num pedido. */
export const NFE_VOID_MAX_RANGE = 1_000;
const MAX_NUMBER = 999_999_999;

/** Condição de uso da CC-e (xCondUso, leiaute da SEFAZ), exibida antes de enviar a correção. */
export const CCE_CONDITIONS =
  "A Carta de Correção é disciplinada pelo § 1º-A do art. 7º do Convênio S/N, de 15 de dezembro de 1970 e pode ser " +
  "utilizada para regularização de erro ocorrido na emissão de documento fiscal, desde que o erro não esteja " +
  "relacionado com: I - as variáveis que determinam o valor do imposto tais como: base de cálculo, alíquota, " +
  "diferença de preço, quantidade, valor da operação ou da prestação; II - a correção de dados cadastrais que " +
  "implique mudança do remetente ou do destinatário; III - a data de emissão ou de saída.";

const HOUR = 3_600_000;

/**
 * Data/hora vinda do provedor ou da SEFAZ em ms. Sem fuso = horário de Brasília (−03:00, o da SEFAZ); inválida = NaN
 * (nunca um prazo infinito ou um `toISOString` que lança na tela).
 */
export function fiscalInstant(s: string): number {
  const t = s.trim();
  const hasTime = /T\d{2}:\d{2}/.test(t);
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(t);
  return Date.parse(hasTime && !hasZone ? `${t}-03:00` : t);
}

/** `fiscalInstant` em ISO (UTC); null quando ausente ou inválida. */
export function fiscalIso(s: string | null | undefined): string | null {
  if (!s) return null;
  const ms = fiscalInstant(s);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export const cancelDeadline = (authorizedAt: string, uf: string): Date =>
  new Date(fiscalInstant(authorizedAt) + (CANCEL_WINDOW_BY_UF[uf] ?? NFE_CANCEL_WINDOW_HOURS) * HOUR);

export const correctionDeadline = (authorizedAt: string): Date =>
  new Date(fiscalInstant(authorizedAt) + NFE_CORRECTION_WINDOW_HOURS * HOUR);

/** Pontuação "tipográfica" comum (copiada de editores) que não existe no Latin-1 do leiaute: vira ASCII. */
const TYPOGRAPHIC: readonly [RegExp, string][] = [
  [/[\u2018\u2019\u201a\u2032]/g, "'"],
  [/[\u201c\u201d\u201e\u2033]/g, '"'],
  [/[\u2010-\u2015\u2212]/g, "-"],
  [/\u2026/g, "..."],
];

/**
 * Espaços e quebras viram um espaço (a SEFAZ não aceita quebra de linha em xJust/xCorrecao); aspas, apóstrofos,
 * travessões e reticências tipográficos viram ASCII.
 */
export const normalizeEventText = (s: string) =>
  TYPOGRAPHIC.reduce((t, [re, to]) => t.replace(re, to), s)
    .replace(/\s+/g, " ")
    .trim();

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/;
/** Fora do `TString` do leiaute (`[!-ÿ]`, Latin-1 imprimível): emoji, símbolos, outros alfabetos. */
const NOT_LATIN1 = /[^\u0020-\u007e\u00a0-\u00ff]/u;

/** Justificativa (cancelamento/inutilização, 15–255) ou correção (CC-e, 15–1000). null = válido. */
export function eventTextProblem(raw: string, kind: "reason" | "correction"): string | null {
  if (CONTROL.test(raw)) return "O texto tem caracteres inválidos";
  const s = normalizeEventText(raw);
  if (NOT_LATIN1.test(s))
    return "O texto tem caracteres que a SEFAZ não aceita (emoji ou símbolos especiais)";
  const what = kind === "reason" ? "A justificativa" : "A correção";
  const max = kind === "reason" ? 255 : 1000;
  if (s.length < 15) return `${what} precisa ter ao menos 15 caracteres`;
  if (s.length > max) return `${what} aceita até ${max} caracteres`;
  return null;
}

/** Faixa da inutilização (mesma série). null = válida. */
export function voidRangeProblem(r: { from: number; to: number }): string | null {
  const ok = (n: number) => Number.isInteger(n) && n >= 1 && n <= MAX_NUMBER;
  if (!ok(r.from) || !ok(r.to)) return "Números de 1 a 999.999.999";
  if (r.from > r.to) return "O número inicial deve ser menor ou igual ao final";
  if (r.to - r.from + 1 > NFE_VOID_MAX_RANGE) return `Inutilize até ${NFE_VOID_MAX_RANGE} números por vez`;
  return null;
}

// ---------- lacunas da numeração ----------

export type NumberedDoc = { id: string; number: number | null; status: string };
export type NfeGap = { from: number; to: number; kind: "gap" | "rejected"; documentIds: string[] };

/** Rejeitada ou rascunho com número: número queimado sem registro na SEFAZ (05 §9). */
const HOLDS_GAP = new Set(["rejected", "draft"]);

/**
 * Números que podem ser inutilizados na série: buracos entre 1 e o maior número usado (sequência avançada à mão) e
 * números de notas rejeitadas/rascunho com número, menos as faixas já inutilizadas (ou em pedido). O(n) nas notas.
 */
export function nfeGaps(
  docs: readonly NumberedDoc[],
  voided: readonly { from: number; to: number }[],
): NfeGap[] {
  const used = docs
    .filter((d): d is NumberedDoc & { number: number } => d.number !== null)
    .sort((a, b) => a.number - b.number);
  const raw: NfeGap[] = [];
  let next = 1;
  for (const d of used) {
    if (d.number > next) raw.push({ from: next, to: d.number - 1, kind: "gap", documentIds: [] });
    if (HOLDS_GAP.has(d.status)) {
      const last = raw[raw.length - 1];
      if (last && last.kind === "rejected" && last.to === d.number - 1) {
        last.to = d.number;
        last.documentIds.push(d.id);
      } else raw.push({ from: d.number, to: d.number, kind: "rejected", documentIds: [d.id] });
    }
    next = Math.max(next, d.number + 1);
  }
  const cuts = [...voided].sort((a, b) => a.from - b.from);
  const out: NfeGap[] = [];
  for (const g of raw) {
    let pieces: { from: number; to: number }[] = [{ from: g.from, to: g.to }];
    for (const c of cuts)
      pieces = pieces.flatMap((p) =>
        c.to < p.from || c.from > p.to
          ? [p]
          : [
              ...(c.from > p.from ? [{ from: p.from, to: c.from - 1 }] : []),
              ...(c.to < p.to ? [{ from: c.to + 1, to: p.to }] : []),
            ],
      );
    for (const p of pieces)
      out.push({
        ...p,
        kind: g.kind,
        documentIds:
          g.kind === "gap" ? [] : g.documentIds.filter((_, k) => g.from + k >= p.from && g.from + k <= p.to),
      });
  }
  return out;
}

// ---------- devolução ----------

/**
 * CFOP da devolução pelo CFOP do item original. Venda (5/6) → devolução de venda de entrada (1/2); compra (1/2) →
 * devolução de compra de saída (5/6), usado pela importação de XML de entrada (T2.5). Sem correspondência = null.
 */
const RETURN_CFOP: Readonly<Record<string, string>> = {
  "5101": "1201",
  "6101": "2201",
  "6107": "2201",
  "5102": "1202",
  "6102": "2202",
  "6108": "2202",
  "5401": "1410",
  "6401": "2410",
  "5403": "1411",
  "5405": "1411",
  "6403": "2411",
  "6404": "2411",
  "1101": "5201",
  "2101": "6201",
  "1102": "5202",
  "2102": "6202",
};
export const returnCfopOf = (cfop: string): string | null => RETURN_CFOP[cfop] ?? null;

/** Parte acumulada de `x` até `n` de `total` (meio para cima): `at(total) = x`. */
function cumulative(x: number, n: number, total: number): number {
  const num = BigInt(x) * BigInt(n) * 2n + BigInt(total);
  return Number(num / (2n * BigInt(total)));
}

/**
 * Percorre os campos em centavos (`*Cents`, menos `unitCents`) com o mesmo campo das devoluções anteriores ainda
 * ativas: `f(original, soma das anteriores)`.
 */
function scaleCents<T>(v: T, priors: readonly unknown[], f: (x: number, prior: number) => number): T {
  if (Array.isArray(v) || v === null || typeof v !== "object") return v;
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    const sub = priors.map((p) =>
      p !== null && typeof p === "object" ? (p as Record<string, unknown>)[k] : undefined,
    );
    out[k] =
      typeof x === "number" && k.endsWith("Cents") && k !== "unitCents"
        ? f(
            x,
            sub.reduce<number>((a, s) => a + (typeof s === "number" ? s : 0), 0),
          )
        : typeof x === "object"
          ? scaleCents(x, sub, f)
          : x;
  }
  return out as T;
}

export type ReturnQty = {
  /** Quantidade do item na nota original. */
  originalQtyQ4: number;
  /** Já devolvida (ou em devolução) antes desta. */
  returnedQtyQ4: number;
  qtyQ4: number;
  unitPriceCents: number;
  /**
   * Impostos das devoluções anteriores ainda ativas deste item. Com elas, cada campo = fração acumulada até o fim
   * desta devolução − o que as ativas já levaram, limitado ao que sobra do original: depois de uma devolução cancelada
   * no meio, a soma das ativas nunca passa do original (e devolver o resto fecha no centavo). Sem elas, a fração
   * acumulada entre `returnedQtyQ4` e o fim.
   */
  prior?: readonly TaxItemResult[];
};

/**
 * Impostos do item devolvido espelhando o original: cada valor em centavos (bases, impostos, frete, desconto…) pela
 * fração acumulada da quantidade, alíquotas iguais, CFOP da devolução; vNF do item refeito pela fórmula da SEFAZ.
 */
export function returnTaxItem(src: TaxItemResult, q: ReturnQty, cfop: string): TaxItemResult {
  if (q.qtyQ4 <= 0 || q.returnedQtyQ4 + q.qtyQ4 > q.originalQtyQ4)
    throw new DomainError("return_qty_exceeded", "Quantidade acima do saldo devolvível do item.");
  const total = q.originalQtyQ4;
  const after = q.returnedQtyQ4 + q.qtyQ4;
  const f = (x: number, priorSum: number) => {
    const taken = q.prior ? priorSum : cumulative(x, q.returnedQtyQ4, total);
    const room = x - taken;
    const want = cumulative(x, after, total) - taken;
    return Math.min(Math.max(want, Math.min(0, room)), Math.max(0, room));
  };
  const r = scaleCents(src, q.prior ?? [], f);
  r.cfop = cfop;
  if (r.ipi && r.ipi.qtyQ4 > 0) r.ipi = { ...r.ipi, qtyQ4: q.qtyQ4 };
  if (r.ibsCbs) r.ibsCbs = { ...r.ibsCbs, ibsCents: r.ibsCbs.ibsUf.valueCents + r.ibsCbs.ibsMun.valueCents };
  r.warnings = [];
  r.totalCents =
    r.grossCents -
    r.discountCents +
    r.freightCents +
    r.insuranceCents +
    r.otherCents +
    (r.icms.st?.valueCents ?? 0) +
    (r.icms.st?.fcpCents ?? 0) +
    (r.ipi?.valueCents ?? 0);
  return r;
}

/** Resultado do motor para a devolução: destino/consumidor final do original, itens espelhados e totais somados. */
export function returnTaxes(
  original: Pick<TaxResult, "destination" | "finalConsumer">,
  items: readonly { index: number; item: TaxItemResult }[],
): TaxResult {
  const list = items.map((x, k) => ({ ...x.item, index: k }));
  return {
    destination: original.destination,
    finalConsumer: original.finalConsumer,
    items: list,
    totals: taxTotalsOf(list),
  };
}
